import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatures } from '../hooks/useFeatures';
import {
  FiHome, FiUsers, FiCalendar, FiBook, FiFileText,
  FiDollarSign, FiBell, FiSettings, FiLogOut, FiMenu, FiX,
  FiUserCheck, FiClipboard, FiActivity, FiMessageCircle, FiInbox, FiCreditCard,
  FiGrid, FiCheckSquare, FiAward
} from 'react-icons/fi';

// 아이콘 문자열을 컴포넌트로 매핑
const iconMap = {
  FiHome,
  FiGrid,
  FiUsers,
  FiUserCheck,
  FiBook,
  FiCalendar,
  FiCheckSquare,
  FiFileText,
  FiAward,
  FiDollarSign,
  FiBell,
  FiCreditCard,
  FiClipboard,
  FiActivity,
  FiMessageCircle,
  FiInbox,
  FiSettings
};

// 강사 메뉴 (작성 주기 설정에 따라 동적으로 필터링)
const getTeacherMenuItems = (frequencies = ['daily']) => {
  const allMenus = [
    { path: '/', icon: FiHome, label: '대시보드' },
    { path: '/classes', icon: FiBook, label: '내 반' },
    { path: '/students', icon: FiUsers, label: '학생 관리' },
    { path: '/attendance', icon: FiCalendar, label: '출결 관리' },
    { path: '/tests', icon: FiAward, label: '시험/성적' },
    { path: '/lessons', icon: FiFileText, label: '일일 리포트' },
    { path: '/weekly-reports', icon: FiCalendar, label: '주간 리포트' },
    { path: '/monthly-reports', icon: FiCalendar, label: '월간 리포트' },
    { path: '/counseling', icon: FiClipboard, label: '상담 내역' },
    { path: '/staff-notice', icon: FiInbox, label: '내부 공지' },
    { path: '/chat', icon: FiMessageCircle, label: '채팅' },
  ];

  // 필터링할 메뉴 경로 매핑
  const frequencyMenuMap = {
    'daily': '/lessons',
    'weekly': '/weekly-reports',
    'monthly': '/monthly-reports'
  };

  // 활성화된 주기에 해당하는 메뉴 경로만 포함
  const allowedPaths = Object.entries(frequencyMenuMap)
    .filter(([frequency, _]) => frequencies.includes(frequency))
    .map(([_, path]) => path);

  // 메뉴 필터링
  return allMenus.filter(menu => {
    const reportPaths = Object.values(frequencyMenuMap);
    if (!reportPaths.includes(menu.path)) {
      return true;
    }
    return allowedPaths.includes(menu.path);
  });
};

// 조교 메뉴 (제한적인 권한)
const assistantMenuItems = [
  { path: '/', icon: FiHome, label: '대시보드' },
  { path: '/students', icon: FiUsers, label: '학생 관리' },
  { path: '/attendance', icon: FiCalendar, label: '출결 관리' },
  { path: '/tests', icon: FiAward, label: '시험/성적' },
  { path: '/staff-notice', icon: FiInbox, label: '내부 공지' },
  { path: '/chat', icon: FiMessageCircle, label: '채팅' },
];

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user, tenant, logout } = useAuth();
  const { menus } = useFeatures();
  const location = useLocation();
  const navigate = useNavigate();

  // 수업일지 작성 주기 설정
  const frequencies = tenant?.lessonReportConfig?.frequencies || ['daily'];

  // Admin 메뉴: operationType에 따라 동적으로 설정
  const adminMenu = menus.map(menu => ({
    path: menu.path,
    label: menu.label,
    icon: iconMap[menu.icon] || FiHome
  }));

  // 강사 메뉴: 작성 주기에 따라 동적으로 생성
  const teacherMenu = getTeacherMenuItems(frequencies);

  // role에 따라 메뉴 결정
  const menuItems = user?.role === 'admin' ? adminMenu :
                    user?.role === 'teacher' ? teacherMenu :
                    user?.role === 'assistant' ? assistantMenuItems :
                    [];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="layout">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <h1 className="logo">{tenant?.name || 'EduStaff'}</h1>
          <button className="toggle-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
            >
              <item.icon className="nav-icon" />
              {sidebarOpen && <span>{item.label}</span>}
            </Link>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">{user?.name?.[0] || 'U'}</div>
            {sidebarOpen && (
              <div className="user-details">
                <span className="user-name">{user?.name}</span>
                <span className="user-role">
                  {user?.role === 'admin' ? '관리자' : '강사'}
                </span>
              </div>
            )}
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <FiLogOut />
            {sidebarOpen && <span>로그아웃</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <header className="top-header">
          <button className="mobile-menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <FiMenu />
          </button>
          <div className="header-right">
            <span className="welcome">안녕하세요, {user?.name}님</span>
          </div>
        </header>
        <div className="content-wrapper">
          {children}
        </div>
      </main>
    </div>
  );
}
